import React from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Paginator } from 'primereact/paginator';
import { Dialog } from 'primereact/dialog';
import type { RequestItem } from 'features/dashboard/types/dashboard';
import '../../../styles/pages.css';
import ViewCOIRequest from './ViewCOIRequest';

export type RequestsListProps = {
  items: RequestItem[];
  onView?: (id: string) => void;
  onApprove?: (id: string) => void;
  onDecline?: (id: string) => void;
  pageSize?: number;
  viewOnly?: boolean;
};

const RequestsList: React.FC<RequestsListProps> = ({ items, onView, onApprove, onDecline, pageSize = 5, viewOnly = false }) => {
  const [first, setFirst] = React.useState(0);
  const rows = pageSize;
  const pagedItems = React.useMemo(() => items.slice(first, first + rows), [items, first, rows]);
  const rowHeight = 64;
  const minRows = rows;
  const scrollHeight = rowHeight * minRows;
  const [confirm, setConfirm] = React.useState<{
    id: string;
    action: 'approve' | 'decline';
  } | null>(null);
  const [viewId, setViewId] = React.useState<string | null>(null);

  const handleConfirm = (id: string, action: 'approve' | 'decline') => setConfirm({ id, action });
  const handleCancel = () => setConfirm(null);
  const handleOk = () => {
    if (confirm) {
      if (confirm.action === 'approve') onApprove?.(confirm.id);
      if (confirm.action === 'decline') onDecline?.(confirm.id);
    }
    setConfirm(null);
  };
  const handleView = (id: string) => {
    if (typeof onView === 'function') {
      onView(id);
    } else {
      setViewId(id);
    }
  };
  const handleHideView = () => setViewId(null);

  if (items.length === 0) {
    return (
      <div className="panel__emptyState">
        <div className="panel__emptyState__iconWrap">
          <i className="pi pi-inbox panel__emptyState__icon" />
        </div>
        <div className="panel__emptyState__title">No requests</div>
        <div className="panel__emptyState__sub">You have no pending certificate requests at this time.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px' }}>
        {pagedItems.map((req) => (
          <div key={req.id} className="requestRow">
            <div className="requestRow__left">
              <div className="requestRow__title">{req.organizationName}</div>
              <div className="page__muted">
                {req.contactName} • {req.contactEmail}
              </div>
              <div className="requestRow__tags">
                <Tag value={req.typeLabel}  style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, height: 20, lineHeight: '16px' }} />
                <span className="page__muted">{req.createdDateLabel}</span>
              </div>
              <div className="page__muted" style={{ marginTop: 8 }}>
                “{req.note}”
              </div>
            </div>
            <div className="requestRow__right">
              <Button
                icon="pi pi-eye"
                label="View"
                className="p-button-outlined p-button-sm outlined-blue"
                onClick={() => handleView(req.id)}
              />
              {!viewOnly && req.status === 'Pending' ? (
                <>
                  <Button
                    icon="pi pi-check"
                    label="Mark as Completed"
                    className="p-button-sm appButton--primary"
                    onClick={() => handleConfirm(req.id, 'approve')}
                  />
                  {/* <Button
                    icon="pi pi-times"
                    className="p-button-outlined p-button-danger p-button-sm"
                    aria-label="Decline"
                    onClick={() => handleConfirm(req.id, 'decline')}
                  /> */}
                </>
              ) : !viewOnly && req.status !== 'Pending' ? (
                <Tag
                  value={req.status === 'Approved' ? 'Completed' : 'Declined'}
                  severity={req.status === 'Approved' ? 'success' : 'danger'}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <div style={{ flex: '0 0 auto', borderTop: '1px solid #e6e7ef', background: '#fff', display: 'flex', justifyContent: 'center', padding: '8px 12px' }}>
        <Paginator
          first={first}
          rows={rows}
          totalRecords={items.length}
          onPageChange={(e) => setFirst(e.first)}
          template="PrevPageLink PageLinks NextPageLink"
          className="requests-paginator"
        />
      </div>
      <Dialog
        header={confirm?.action === 'approve' ? 'Mark as Completed' : 'Decline Request'}
        visible={!!confirm}
        style={{ width: 320 }}
        onHide={handleCancel}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancel" className="p-button-text" onClick={handleCancel} autoFocus />
            <Button
              label={confirm?.action === 'approve' ? 'Mark as Completed' : 'Decline'}
              className={confirm?.action === 'approve' ? 'p-button-success' : 'p-button-danger'}
              onClick={handleOk}
            />
          </div>
        }
        closable={false}
        modal
      >
        <div style={{ fontSize: 14 }}>
          Are you sure you want to {confirm?.action === 'approve' ? 'mark as completed' : 'decline'} this request?
        </div>
      </Dialog>
      {!onView && (
        <ViewCOIRequest visible={!!viewId} onHide={handleHideView} databaseId={viewId} />
      )}
    </div>
  );
};

export default RequestsList;
